/**
 * Wspólne walidacje dla formularzy użytkownika
 * Używane w SignUp i AccountSettings
 */

export interface ValidationResult {
  isValid: boolean;
  errors: Record<string, string>;
}

/**
 * Waliduje podstawowe pola użytkownika
 */
export const validateUserFields = (formData: Record<string, any>): ValidationResult => {
  const errors: Record<string, string> = {};

  // Wymagane pola - sprawdzamy również pusty string po trim()
  const requiredFields = ["firstName", "lastName", "organizationName"];
  requiredFields.forEach((field) => {
    const value = formData[field];
    if (!value || (typeof value === "string" && !value.trim())) {
      errors[field] = "To pole jest wymagane.";
    }
  });

  // Walidacja email (wymagane) - sprawdzamy również pusty string po trim()
  const emailValue = formData.email;
  if (!emailValue || (typeof emailValue === "string" && !emailValue.trim())) {
    errors.email = "To pole jest wymagane.";
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue.trim())) {
    errors.email = "Nieprawidłowy format adresu email.";
  }

  /**
   * Waliduje format imienia lub nazwiska użytkownika
   * Sprawdza: długość (2-100 znaków), spacje na początku/końcu, podwójne spacje, myślniki na początku/końcu, wielokrotne myślniki
   */
  const validateUserNameFormat = (name: string, fieldName: string): string | null => {
    if (!name || !name.trim()) {
      return null; // Pusta wartość jest obsługiwana przez inną walidację
    }

    const trimmedName = name.trim();

    // Sprawdź długość (min 2, max 100 znaków)
    if (trimmedName.length < 2) {
      return `${fieldName} musi mieć co najmniej 2 znaki`;
    }
    if (trimmedName.length > 50) {
      return `${fieldName} nie może mieć więcej niż 50 znaków`;
    }

    // Sprawdź czy zawiera tylko dozwolone znaki (litery, spacje, myślniki)
    if (!/^[A-ZĄĆĘŁŃÓŚŹŻa-ząćęłńóśźż\s-]+$/.test(trimmedName)) {
      return `${fieldName} może zawierać tylko litery, spacje i znak "-"`;
    }

    // Sprawdź czy zaczyna się od spacji
    if (name.startsWith(" ")) {
      return `${fieldName} nie może zaczynać się od spacji`;
    }

    // Sprawdź czy kończy się spacją
    if (name.endsWith(" ")) {
      return `${fieldName} nie może kończyć się spacją`;
    }

    // Sprawdź czy ma podwójne spacje
    if (trimmedName.includes("  ")) {
      return `${fieldName} nie może zawierać podwójnych spacji`;
    }

    // Sprawdź czy zaczyna się od "-"
    if (trimmedName.startsWith("-")) {
      return `${fieldName} nie może zaczynać się od znaku "-"`;
    }

    // Sprawdź czy kończy się na "-"
    if (trimmedName.endsWith("-")) {
      return `${fieldName} nie może kończyć się znakiem "-"`;
    }

    // Sprawdź czy ma ciąg dwóch lub więcej znaków "-" obok siebie
    if (trimmedName.includes("--")) {
      return `${fieldName} nie może zawierać wielokrotnych znaków "-" obok siebie`;
    }

    return null;
  };

  // Walidacja imienia (tylko jeśli wartość nie jest pusta po trim())
  if (formData.firstName && typeof formData.firstName === "string" && formData.firstName.trim()) {
    const firstNameError = validateUserNameFormat(formData.firstName, "Imię");
    if (firstNameError) {
      errors.firstName = firstNameError;
    }
  }

  // Walidacja nazwiska (tylko jeśli wartość nie jest pusta po trim())
  if (formData.lastName && typeof formData.lastName === "string" && formData.lastName.trim()) {
    const lastNameError = validateUserNameFormat(formData.lastName, "Nazwisko");
    if (lastNameError) {
      errors.lastName = lastNameError;
    }
  }

  // Walidacja nazwy organizacji
  if (formData.organizationName && formData.organizationName.length < 2) {
    errors.organizationName = "Nazwa organizacji musi mieć co najmniej 2 znaki.";
  }

  // Walidacja numeru telefonu (wymagane w formularzu rejestracji) - sprawdzamy również pusty string po trim()
  const phoneValue = formData.phoneNumber;
  if (!phoneValue || (typeof phoneValue === "string" && !phoneValue.trim())) {
    errors.phoneNumber = "To pole jest wymagane.";
  } else if (!/^[\+]?[0-9\s\-\(\)]{9,}$/.test(phoneValue.trim())) {
    errors.phoneNumber = "Nieprawidłowy format numeru telefonu.";
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

/**
 * Waliduje pola hasła
 */
export const validatePasswordFields = (formData: Record<string, any>): ValidationResult => {
  const errors: Record<string, string> = {};

  /**
   * Waliduje siłę hasła
   * Wymagania: min 8 znaków, duże litery, małe litery, liczby, znaki specjalne
   */
  const validatePasswordStrength = (password: string): string[] => {
    const passwordErrors: string[] = [];

    // Minimum 8 znaków
    if (password.length < 8) {
      passwordErrors.push("Hasło musi mieć co najmniej 8 znaków.");
    }

    // Duże litery
    if (!/[A-ZĄĆĘŁŃÓŚŹŻ]/.test(password)) {
      passwordErrors.push("Hasło musi zawierać co najmniej jedną wielką literę.");
    }

    // Małe litery
    if (!/[a-ząćęłńóśźż]/.test(password)) {
      passwordErrors.push("Hasło musi zawierać co najmniej jedną małą literę.");
    }

    // Liczby
    if (!/[0-9]/.test(password)) {
      passwordErrors.push("Hasło musi zawierać co najmniej jedną cyfrę.");
    }

    // Znaki specjalne
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      passwordErrors.push("Hasło musi zawierać co najmniej jeden znak specjalny.");
    }

    return passwordErrors;
  };

  // Walidacja hasła (wymagane) - sprawdzamy również pusty string po trim()
  const passwordValue = formData.password;
  if (!passwordValue || (typeof passwordValue === "string" && !passwordValue.trim())) {
    errors.password = "To pole jest wymagane.";
  } else {
    const passwordErrors = validatePasswordStrength(passwordValue);
    if (passwordErrors.length > 0) {
      errors.password = passwordErrors.join(" ");
    }
  }

  // Walidacja powtórzenia hasła (wymagane) - sprawdzamy również pusty string po trim()
  const repeatedPasswordValue = formData.repeatedPassword;
  if (
    !repeatedPasswordValue ||
    (typeof repeatedPasswordValue === "string" && !repeatedPasswordValue.trim())
  ) {
    errors.repeatedPassword = "To pole jest wymagane.";
  } else if (passwordValue && passwordValue !== repeatedPasswordValue) {
    errors.repeatedPassword = "Hasła nie są takie same!";
  }

  // Walidacja nowego hasła (dla zmiany hasła)
  if (formData.newPassword) {
    const newPasswordErrors = validatePasswordStrength(formData.newPassword);
    if (newPasswordErrors.length > 0) {
      errors.newPassword = newPasswordErrors.join(" ");
    }
  }

  // Walidacja powtórzenia nowego hasła
  if (formData.repeatedNewPassword && formData.newPassword !== formData.repeatedNewPassword) {
    errors.repeatedNewPassword = "Hasła nie są takie same!";
  }

  // Walidacja starego hasła (dla zmiany hasła)
  if (
    (formData.oldPassword || formData.newPassword || formData.repeatedNewPassword) &&
    !formData.oldPassword
  ) {
    errors.oldPassword = "Podaj stare hasło.";
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

/**
 * Waliduje dane faktury
 */
export const validateInvoiceData = (formData: Record<string, any>): ValidationResult => {
  const errors: Record<string, string> = {};

  // Jeśli faktura nie jest potrzebna, nie walidujemy
  if (!formData.isInvoiceNeeded) {
    return { isValid: true, errors: {} };
  }

  // Wymagane pola faktury
  const requiredInvoiceFields = [
    "invoiceName",
    "invoiceTaxId",
    "invoiceStreet",
    "invoiceHouseNumber",
    "invoicePostalCode",
    "invoiceCity",
  ];

  requiredInvoiceFields.forEach((field) => {
    if (!formData[field]) {
      errors[field] = "To pole jest wymagane.";
    }
  });

  // Walidacja NIP (10 cyfr)
  if (formData.invoiceTaxId && !/^[0-9]{10}$/.test(formData.invoiceTaxId.replace(/[\s-]/g, ""))) {
    errors.invoiceTaxId = "NIP musi składać się z 10 cyfr.";
  }

  // Walidacja kodu pocztowego (XX-XXX)
  if (formData.invoicePostalCode && !/^[0-9]{2}-[0-9]{3}$/.test(formData.invoicePostalCode)) {
    errors.invoicePostalCode = "Kod pocztowy musi być w formacie XX-XXX.";
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

/**
 * Waliduje wszystkie pola formularza użytkownika (włącznie z hasłami)
 * Używane w SignUp
 */
export const validateUserForm = (formData: Record<string, any>): ValidationResult => {
  const userValidation = validateUserFields(formData);
  const passwordValidation = validatePasswordFields(formData);
  const invoiceValidation = validateInvoiceData(formData);

  const allErrors = {
    ...userValidation.errors,
    ...passwordValidation.errors,
    ...invoiceValidation.errors,
  };

  return {
    isValid: Object.keys(allErrors).length === 0,
    errors: allErrors,
  };
};

/**
 * Waliduje tylko dane profilu (bez haseł)
 * Używane w AccountSettings przy zapisie profilu
 */
export const validateProfileForm = (formData: Record<string, any>): ValidationResult => {
  const userValidation = validateUserFields(formData);
  const invoiceValidation = validateInvoiceData(formData);

  const allErrors = {
    ...userValidation.errors,
    ...invoiceValidation.errors,
  };

  return {
    isValid: Object.keys(allErrors).length === 0,
    errors: allErrors,
  };
};
